import {Box, Button, Flex, Spinner} from '@chakra-ui/react';
import {getGeoByCoords, getInDeliveryArea} from 'api/requests';
import {DEFAULT_COORDINATES_OBJ} from 'config';
import {useToast, useTranslation} from 'hooks';
import debounce from 'lodash.debounce';
import dynamic from 'next/dynamic';
import {FC, memo, useCallback, useEffect, useState} from 'react';
import {colors} from 'theme';
import {ICoords} from 'types/entities/geo';
import ImageUI from 'ui/ImageUI';
import logger from 'utils/logger';

import {LocationSearch} from './components';

interface Props {
  locationAddress?: any;
  onLocationChange: (locationAddress?: any) => void;
  isCourierExpress?: boolean;
}

const YMapCopyrightPaneClassName = '.leaflet-control-attribution';

const transformMapBuildingToLocationAddress = (building: any) => {
  if (!building?.centroid?.lat) {
    logger.addBreadcrumb('buildingToLocation', building);
  }
  const location = {
    street: building?.street_name,
    number: building?.number,
    latitude: building?.centroid?.lat,
    longitude: building?.centroid?.lon,
    city: building?.location,
    geocoding_source: 'point.md',
  };
  return location;
};

let focusNewCoords = false;

const DynamicMap = dynamic(() => import('./MapUI'), {ssr: false});

export const Map: FC<Props> = memo(({locationAddress, onLocationChange, isCourierExpress}) => {
  const defaultCoords = locationAddress
    ? {lat: locationAddress.latitude, lon: locationAddress.longitude}
    : DEFAULT_COORDINATES_OBJ;
  const [coords, setCoords] = useState<ICoords | undefined>();
  const [spinnerLoading, setSpinnerLoading] = useState(false);
  const [selectedBuildingFromSearch, setSelectedBuildingFromSearch] = useState();
  const toast = useToast();
  const {t} = useTranslation();
  const [showMap, setShowMap] = useState(false);

  const findLocationByCoords = async (coords: ICoords) => {
    logger.logDebug(coords, 'findLocationByCoords');

    try {
      const location: any = await getGeoByCoords(coords);
      setCoords(coords);

      const isDeliverableLocation = await getInDeliveryArea(
        coords,
        `&area_type=${isCourierExpress ? 'b2b' : 'normal'}`,
      );
      if (!['outside_delivery_area', 'outside'].includes(isDeliverableLocation?.result)) {
        onLocationChange(transformMapBuildingToLocationAddress(location.building));
      } else if (location) {
        onLocationChange();
        toast.error(t('LOCATION_OUTSIDE_DELIVERY'));
      }
    } catch (error) {
      onLocationChange();
      toast.error(t('LOCATION_SEARCH_ERROR'));
    } finally {
      setSpinnerLoading(false);
    }
  };

  const debounceSetSpinner = useCallback(() => {
    if (!spinnerLoading) setSpinnerLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    logger.logDebug(selectedBuildingFromSearch, 'useEffect');
    if (!selectedBuildingFromSearch) return;
    onLocationChange(transformMapBuildingToLocationAddress(selectedBuildingFromSearch));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuildingFromSearch, debounceSetSpinner]);

  /**
   * Now this function will be called not more that 1/1500 and will avoid unused calls
   * best solution for now
   */
  const debouncedFindLocationByCoords = debounce(findLocationByCoords, 1800);

  const handleBoundsChange = useCallback((mapCoords: string[]) => {
    logger.logDebug(mapCoords, 'handleBoundsChange');

    const [lat, lon] = mapCoords;
    if (!lat) return;
    const newCoords = {
      lat: Number(lat),
      lon: Number(lon),
    };
    const isSameOrigin = coords?.lat === newCoords.lat && coords?.lon === newCoords.lon;
    if (isSameOrigin) return;
    debouncedFindLocationByCoords(newCoords);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCurrentLocation = () => {
    if (!navigator.geolocation) return toast.warn(t('GEOLOCATION_NOT_SUPPORTED'));

    try {
      debounceSetSpinner();
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const currentLocationCoords = {
            lat: position.coords.latitude,
            lon: position.coords.longitude,
          };
          findLocationByCoords(currentLocationCoords).then(() => {
            focusNewCoords = true;
            setCoords(coords);
          });
        },
        ({code}) => {
          setSpinnerLoading(false);
          if (code === 1) toast.warn(t('REQUIRE_GEOLOCATION_PERMISSIONS'));
        },
      );
    } catch (error) {
      setSpinnerLoading(false);
      toast.warn(t('GEOLOCATION_NOT_SUPPORTED'));
    }
  };

  return (
    <Flex
      position="relative"
      justifyContent="center"
      alignItems="center"
      h={showMap ? '448px' : '90px'}
      sx={{
        [YMapCopyrightPaneClassName]: {
          display: 'none',
        },
      }}
      bg={colors.white}
      w="full"
    >
      {showMap ? (
        <Box width="100%" height="100%" zIndex={0}>
          <DynamicMap
            defaultCoords={{lat: defaultCoords.lat, lng: defaultCoords.lon}}
            isFromCourierExpress={isCourierExpress}
            focusNewCoords={focusNewCoords}
            handleNewCoords={(coords) => {
              focusNewCoords = false;
              debounceSetSpinner();
              handleBoundsChange([coords?.lat?.toString(), coords?.lng?.toString()]);
            }}
            zoom={10}
          />
        </Box>
      ) : (
        <></>
      )}

      {showMap ? (
        <>
          {spinnerLoading && (
            <Spinner color={colors.pink} pointerEvents="none" position="absolute" top="calc(50% - 97px)" />
          )}
          <ImageUI pointerEvents="none" position="absolute" src="/map/pin.svg" alt="location" top="calc(50% - 67px)" />
        </>
      ) : null}
      <LocationSearch
        locationAddress={locationAddress}
        onChange={onLocationChange}
        setSelectedBuildingFromSearch={setSelectedBuildingFromSearch}
        setCoords={(coords: any) => {
          setCoords(coords);
          focusNewCoords = true;
        }}
        isLoading={spinnerLoading}
        isError={false}
        showMap={() => setShowMap(true)}
        mapShowed={showMap}
      />
      {showMap ? (
        <>
          <Button
            position="absolute"
            w="45px"
            bg="white"
            boxShadow="0px 0px 10px rgba(151, 151, 151, 0.4)"
            border="1px solid transparent"
            bottom="60px"
            right="10px"
            p={0}
            onClick={handleCurrentLocation}
          >
            <ImageUI src="/icons/navigation.svg" alt="Current location" />
          </Button>
          <ImageUI src="/map-md-logo.svg" position="absolute" alt="Map.md logo" bottom="10px" right="10px" />
        </>
      ) : null}
    </Flex>
  );
});
